import { IonIcon } from '@ionic/react';
import {
  swapHorizontalOutline,
  calculatorOutline,
  logInOutline,
  refreshCircleOutline,
} from 'ionicons/icons';
import type { GetLeagueEventsQuery } from '../../../../types/graphql-generated';
import { TimelineCard } from '../../components/TimelineCard';
import { ReleaseClauseBadge } from '../../components/ReleaseClauseBadge';

type LeagueEvent = GetLeagueEventsQuery['fantasyLeagueEvents'][0];

interface LeagueEventCardProps {
  event: LeagueEvent;
  activityLastReadAt?: string | null;
}

const FREE_AGENCY_NAME = 'Futyclub';

const EVENT_ICONS: Record<string, string> = {
  PlayerSoldEvent: swapHorizontalOutline,
  ComputedPointsEvent: calculatorOutline,
  NewMemberEvent: logInOutline,
  RostersRestartedEvent: refreshCircleOutline,
};

export const LeagueEventCard = ({
  event,
  activityLastReadAt,
}: LeagueEventCardProps) => {
  const getPlayerName = () => {
    if (!event.player) return 'Jugador desconocido';
    const nick = event.player.nick;
    const teamName = event.player.team?.nameShow;
    return teamName ? `${nick} (${teamName})` : nick;
  };

  const getTeamName = (
    team?: { teamName?: string | null } | null
  ): string => {
    return team?.teamName || FREE_AGENCY_NAME;
  };

  const renderEventContent = () => {
    switch (event.type) {
      case 'PlayerSoldEvent':
        return (
          <div className="space-y-1">
            <p className="text-sm">
              <span className="font-bold">{getPlayerName()}</span>
              {' '}vendido
            </p>
            <p className="text-sm">
              De{' '}
              <span className="font-bold">{getTeamName(event.fantasyTeamFrom)}</span>
              {' '}a{' '}
              <span className="font-bold">{getTeamName(event.fantasyTeamTo)}</span>
            </p>
            <p className="flex items-center gap-1.5 text-sm">
              Precio:  <span className="font-bold">{event.price}€</span>
              {event.releaseClause && <span className="ml-3"><ReleaseClauseBadge /></span>}
            </p>
          </div>
        );

      case 'ComputedPointsEvent':
        return (
          <div>
            <p className="text-sm">Puntos calculados</p>
            <p className="text-sm">Se han calculado los puntos de la jornada</p>
          </div>
        );

      case 'NewMemberEvent': {
        const memberName =
          event.fantasyLeagueMember?.teamName ||
          'Usuario desconocido';
        return (
          <div>
            <p className="text-sm">
              <span className="font-bold">{memberName}</span>
              {' '}se ha unido a la liga
            </p>
          </div>
        );
      }

      case 'RostersRestartedEvent':
        return (
          <div>
            <p className="text-sm">Plantillas reiniciadas</p>
            <p className="text-sm">Todas las plantillas han sido reiniciadas</p>
          </div>
        );

      default:
        return (
          <div>
            <p className="text-sm">Evento desconocido</p>
          </div>
        );
    }
  };

  const icon = EVENT_ICONS[event.type];

  return (
    <TimelineCard createdAt={event.createdAt} lastReadAt={activityLastReadAt}>
      <div className="flex items-start gap-2">
        {icon && (
          <IonIcon
            icon={icon}
            style={{ color: '#C8A951', fontSize: '20px', flexShrink: 0, marginTop: '2px' }}
          />
        )}
        <div>{renderEventContent()}</div>
      </div>
    </TimelineCard>
  );
};
